// Floor plans: cores, corridors, flats and the rooms inside them.
//
// Every wing is laid out in its own frame: s along its plot edge, t inward
// from it. One *typical plan* is cut per wing, from the main body, and every
// storey intersects that plan with its own footprint. That is what makes
// cores stack and flats repeat floor to floor, as they must, while a setback
// storey keeps the same partition walls and simply loses what overhangs.
//
// The sun decides two things in the typical plan, the way it decides them
// for an architect: which band of a corridor wing takes the cores (the one
// that gets less sun, so the flats get more), and which facade of a through
// flat its living room and balcony face.
import { MIN_DWELLING_AREA } from "../rules/ro.ts";
import { hallAllowance } from "./rooms.ts";
import { composeSingle, composeThrough, frontage } from "./bays.ts";
import { REFERENCE } from "./reference.ts";
import { area, difference, distanceTo, distanceToOutline, edges, grow, intersection, join, nearestOnOutline, strip, union, type MultiPoly, type Pt } from "./geom.ts";
import type { Envelope } from "./envelope.ts";
import { entranceOpening, facadeEdges, flatOpenings, reachableRooms, shopOpenings, type Opening } from "./openings.ts";
import { packStalls } from "./parking.ts";
import type { Option } from "./massing.ts";
import type { Programme, UnitType } from "./rules.ts";
import { DEFAULT_LATITUDE, solsticeSun, sunHours, type Mass, type SunSample } from "./sun.ts";

export type UnitKind = "apartment" | "core" | "corridor" | "retail" | "parking";

/** What a core is made of. The rest of the core is shafts and storage (or the entrance hall on the ground floor). */
export type CorePartKind = "stair" | "lift" | "landing" | "shaft";
export interface CorePart { kind: CorePartKind; polygon: MultiPoly }

export type RoomKind = "living" | "bedroom" | "kitchen" | "bath" | "hall" | "storage";
export interface Room { kind: RoomKind; polygon: MultiPoly }

/** A window wall of a flat: where it is and which way it faces. */
export interface Window { at: Pt; normal: Pt; living: boolean }

export interface Unit {
  id: string;
  kind: UnitKind;
  type?: UnitType;
  storey: number;
  polygon: MultiPoly;
  area: number; // gross, wall to wall
  /** Apartments: where the front door is, on a corridor or a core landing. */
  door?: Pt;
  /** Cores on the ground floor: where the building entrance is, on the facade. */
  entrance?: Pt;
  /** Cores: stair, lifts, landing and shafts. */
  parts?: CorePart[];
  /** Apartments: the balcony, outside the facade and inside the envelope. */
  balcony?: MultiPoly;
  /** A core in the inner corner of two wings: reached through the building, not from the street. */
  corner?: boolean;
  /** Windows, glazed and front doors, interior doors, shopfronts, building entrances. */
  openings?: Opening[];
  /** A podium parking floor: its stalls, aisles and the ramp to the next level. */
  stalls?: MultiPoly[];
  aisles?: MultiPoly;
  ramp?: MultiPoly;
  /** Apartments: the rooms, schematic: living, bedrooms, bath, hall. */
  rooms?: Room[];
  windows?: Window[];
  /** Apartments: length of window wall along the wing, as planned (both facades for a through flat). */
  frontage?: number;
  /** Apartments: hours of direct sun on the living room window on 21 December. */
  sunHours?: number;
  usable?: number; // apartments only
  /** Which generator drew the flat: a corridor band, a through band, a corner, a tower. */
  origin?: "corridor" | "through" | "corner" | "tower";
  legalMin?: number;
  ok?: boolean;
}

export interface FloorPlan {
  index: number;
  units: Unit[];
}

export interface Layout {
  floors: FloorPlan[];
  byType: { type: UnitType; count: number; minUsable: number; avgUsable: number; belowMin: number }[];
  total: number;
  usable: number;
  cores: number;
  /** Flats whose living room gets less than SUN_HOURS of direct sun on 21 December. */
  sunless: number;
  /** Usable area of the flats that do get it: what a ranking should reward. */
  sunlitUsable: number;
  /** Stacks of balconies taken off so the drawn ones keep POT inside its limit. */
  balconiesRemoved?: number;
}

/** Usable over wall-to-wall area inside one flat (internal walls, shafts). */
export const UNIT_NET = 0.88;
/** A wing at least this deep gets a central corridor with flats either side. */
export const CORRIDOR_FROM = 14;
export const CORRIDOR_WIDTH = 1.6;
/** Stair + lift + landing, measured along the wing: 2.8 m stair, 1.7 m lift, walls. */
export const CORE_WIDTH = 5.4;
/** Longest run of facade one core may serve before another is needed. */
export const CORE_SPACING = 32;
/**
 * Flats a floor reached straight from one core landing, in a wing with no
 * corridor: one each side. A third flat in the run between two cores would
 * touch neither, and a flat with no door onto circulation is not a flat.
 */
export const FLATS_PER_CORE = 2;
/** How close a door must be to a corridor or core to count as opening onto it. */
const DOOR_TOLERANCE = 0.15;
/** OMS 119/2014: living rooms need 1½ h of direct sun at the winter solstice. */
export const SUN_HOURS = 1.5;
/** Hall, bath and storage along the entrance side of a single-aspect flat. */
const SERVICE_DEPTH = 2.4;
const BATH_WIDTH = 2.3;
/** Depth of the hall-and-bath strip mid-depth in a through flat. */
const SERVICE_STRIP = 1.6;
const MIN_BEDROOM_WIDTH = 2.7;
const MIN_LIVING_WIDTH = 3.6;

interface Frame { a: Pt; u: Pt; n: Pt }

function frame(a: Pt, b: Pt): Frame {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const u: Pt = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
  return { a, u, n: [-u[1], u[0]] };
}
const toWorld = (f: Frame, s: number, t: number): Pt => [f.a[0] + f.u[0] * s + f.n[0] * t, f.a[1] + f.u[1] * s + f.n[1] * t];
const toLocal = (f: Frame, p: Pt): Pt => {
  const dx = p[0] - f.a[0], dy = p[1] - f.a[1];
  return [dx * f.u[0] + dy * f.u[1], dx * f.n[0] + dy * f.n[1]];
};
const rect = (f: Frame, s0: number, s1: number, t0: number, t1: number): MultiPoly =>
  [[[toWorld(f, s0, Math.min(t0, t1)), toWorld(f, s1, Math.min(t0, t1)), toWorld(f, s1, Math.max(t0, t1)), toWorld(f, s0, Math.max(t0, t1))]]];
const neg = (p: Pt): Pt => [-p[0], -p[1]];

/** A bedroom larger than this (wall to wall, about 19 m² net) is a bedroom and its dressing. */
const BEDROOM_MAX = 21;
/** A living room with its kitchen larger than this (wall to wall) is a living room and a kitchen waiting to be drawn. */
const LIVING_MAX = 42;
/** A kitchen cut from an oversized living room: a bay on the facade, and what the living room must keep. */
const KITCHEN_CUT = { width: 2.3, depth: 4.2, min: 7, living: 20 };

/**
 * A bay cut from a room along its longest window wall, `CUT.width` of facade
 * and up to `CUT.depth` deep, at the end nearer `door` (or farther, if not
 * `nearDoor`). Refused if either piece comes out in two parts, the bay is
 * too small, or the rest loses its window wall. Used for a kitchen of its own
 * in an oversized living room.
 */
function carveBay(room: MultiPoly, storey: MultiPoly, door: Pt | undefined, CUT: { width: number; depth: number; min: number; living: number }, nearDoor = false): { bay: MultiPoly; rest: MultiPoly } | undefined {
  const walls = facadeEdges(room, storey).map(([a, b]) => ({ a, b, L: Math.hypot(b[0] - a[0], b[1] - a[1]) })).filter((w) => w.L >= CUT.width + 3.6).sort((x, y) => y.L - x.L);
  for (const w of walls) {
    // the end of the wall farther from the front door goes first
    const ends: [Pt, Pt][] = [[w.a, w.b], [w.b, w.a]];
    if (door) ends.sort((x, y) => (nearDoor ? -1 : 1) * (Math.hypot(y[0][0] - door[0], y[0][1] - door[1]) - Math.hypot(x[0][0] - door[0], x[0][1] - door[1])));
    for (const [a, b] of ends) {
      let f = frame(a, b);
      // n must point into the room
      const probe = toWorld(f, w.L / 2, 0.2);
      if (distanceTo(probe, room) > 0) f = { ...f, n: neg(f.n) };
      const deep = Math.min(CUT.depth, extent(f, room).t1);
      if (deep < 2.7) continue;
      const bay = intersection(rect(f, 0, CUT.width, 0, deep), room);
      const rest = difference(room, bay);
      if (bay.length !== 1 || rest.length !== 1) continue;
      if (area(bay) < CUT.min || area(rest) < CUT.living) continue;
      if (!facadeEdges(rest, storey).some(([p, q]) => Math.hypot(q[0] - p[0], q[1] - p[1]) >= 1.3)) continue;
      return { bay, rest };
    }
  }
  return undefined;
}

function extent(f: Frame, mp: MultiPoly) {
  let s0 = Infinity, s1 = -Infinity, t0 = Infinity, t1 = -Infinity;
  for (const poly of mp) for (const p of poly[0]) {
    const [s, t] = toLocal(f, p);
    s0 = Math.min(s0, s); s1 = Math.max(s1, s); t0 = Math.min(t0, t); t1 = Math.max(t1, t);
  }
  return { s0, s1, t0, t1 };
}

/** One cell of the typical plan, in world coordinates, before clipping. */
interface Cell {
  kind: UnitKind;
  /** Which generator drew it, for measuring each against the reference. */
  origin?: Unit["origin"];
  type?: UnitType;
  shape: MultiPoly;
  wing: number;
  door?: Pt;
  entrance?: Pt;
  parts?: CorePart[];
  balcony?: MultiPoly;
  rooms?: Room[];
  windows?: Window[];
  frontage?: number;
  /** Cores of a through wing: the smaller core above the ground floor, where no entrance hall is needed. */
  upper?: { shape: MultiPoly; parts: CorePart[] };
  /** Flats next to such a core: the piece of the ground-floor core they take over above it. */
  extra?: MultiPoly;
  /** A core in the inner corner of two wings: reached through the building, not from the street. */
  corner?: boolean;
}

/** A stair with two flights and a well, and a lift car with its shaft walls. */
export const STAIR = { width: 2.8, length: 5.6 };
export const LIFT = { width: 1.7, depth: 1.8 };
/** A second lift above this many storeys above ground. */
export const SECOND_LIFT_ABOVE = 8;

/**
 * The inside of one core, in the wing's frame, between t0 and t1. The
 * landing sits where the flat doors are, `landingAt` (mid-depth in a through
 * wing, against the corridor in a corridor wing); stair and lifts run from
 * it towards `dir`; what is left is shafts and storage, and on the ground
 * floor the hall from the entrance on the facade at `facade`.
 */
function coreParts(f: Frame, c: number, t0: number, t1: number, landing: [number, number], dir: 1 | -1, lifts: number, facade: number): { parts: CorePart[]; entrance: Pt } {
  const w = CORE_WIDTH / 2;
  const parts: CorePart[] = [{ kind: "landing", polygon: rect(f, c - w, c + w, landing[0], landing[1]) }];
  const from = dir === 1 ? landing[1] : landing[0];
  const stairEnd = dir === 1 ? Math.min(t1, from + STAIR.length) : Math.max(t0, from - STAIR.length);
  parts.push({ kind: "stair", polygon: rect(f, c - w, c - w + STAIR.width, from, stairEnd) });
  for (let i = 0; i < lifts; i++) {
    const a = from + dir * i * (LIFT.depth + 0.1), b = a + dir * LIFT.depth;
    parts.push({ kind: "lift", polygon: rect(f, c - w + STAIR.width + 0.3, c - w + STAIR.width + 0.3 + LIFT.width, a, b) });
  }
  parts.push({ kind: "shaft", polygon: difference(rect(f, c - w, c + w, t0, t1), union(...parts.map((p) => p.polygon))) });
  // the entrance opens into what is left on the facade side: the hall
  return { parts, entrance: toWorld(f, c + w - 1.2, facade) };
}

type MixEntry = Programme["mix"][number];

/**
 * How a run of flats reaches circulation. Off a corridor every flat has a
 * door on the corridor side. Off core landings only the flat touching each
 * core does, so the run holds one flat per adjacent core.
 */
type Access =
  | { by: "corridor"; t: number }
  | { by: "landing"; left: boolean; right: boolean; t: number };

/**
 * Picks flat types so that the running count tracks the mix: always the
 * type furthest behind its share. A shared counter across wings keeps the
 * whole floor on the mix, not each wing separately.
 */
class MixCounter {
  placed = new Map<UnitType, number>();
  total = 0;
  constructor(private mix: Programme["mix"]) {}
  next(fits: (t: UnitType) => boolean): Programme["mix"][number] | undefined {
    const ranked = [...this.mix].sort((x, y) =>
      (y.share * (this.total + 1) - (this.placed.get(y.type) ?? 0)) - (x.share * (this.total + 1) - (this.placed.get(x.type) ?? 0)) || x.area - y.area);
    return ranked.find((m) => fits(m.type));
  }
  take(t: UnitType) { this.placed.set(t, (this.placed.get(t) ?? 0) + 1); this.total++; }
}

/**
 * The flats of a run that opens off core landings: exactly one per
 * adjacent core. Every combination of types is tried, and the one that
 * fits the run with the least stretching wins, the mix breaking near-ties,
 * because with two flats there is no room to correct a bad fit later.
 */
function landingRun(len: number, d: number, count: number, prog: Programme, mix: MixCounter): { m: MixEntry; k: number }[] {
  const width = (m: MixEntry) => m.area / UNIT_NET / d;
  const combos: MixEntry[][] = count === 1 ? prog.mix.map((m) => [m]) : prog.mix.flatMap((a) => prog.mix.map((b) => [a, b]));
  const deficit = (m: MixEntry) => m.share * (mix.total + 1) - (mix.placed.get(m.type) ?? 0);
  let best: { combo: MixEntry[]; k: number; score: number } | undefined;
  for (const combo of combos) {
    const k = len / combo.reduce((t, m) => t + width(m), 0);
    if (!combo.every((m) => m.area * k >= MIN_DWELLING_AREA[m.type])) continue;
    const score = Math.abs(Math.log(k)) - 0.3 * combo.reduce((t, m) => t + deficit(m), 0);
    if (!best || score < best.score) best = { combo, k, score };
  }
  if (!best) return [];
  best.combo.forEach((m) => mix.take(m.type));
  return best.combo.map((m) => ({ m, k: best!.k }));
}

/** Where a room nobody can enter goes, by preference, then into any neighbour but the bath: a living room absorbs the kitchen beside it (an open kitchen). */
const MERGE_INTO: Record<RoomKind, RoomKind[]> = {
  living: ["kitchen", "hall"],
  bedroom: ["living", "bedroom", "storage"],
  kitchen: ["living", "hall"],
  hall: ["living", "hall"],
  storage: ["hall", "living", "bedroom", "kitchen", "storage"],
  bath: ["hall"],
};
/** Which name a merged room keeps. */
const RANK: RoomKind[] = ["living", "bedroom", "kitchen", "bath", "hall", "storage"];

/**
 * A room is one piece. Cuts and merges can leave a room in two parts, or
 * two parts that touch only at a corner (a hall either side of a bath set in
 * its elbow); the larger keeps the name, and a piece broken off the living
 * room is the kitchen the flat lacks, else a store.
 */
function splitRooms(u: Unit): void {
  if (!u.rooms) return;
  const out: Room[] = [];
  for (const r of u.rooms) {
    // pieces apart, or joined only at a corner: shrunk by 30 cm a pinch comes undone
    let parts = r.polygon.filter((p) => area([p]) > 0.3);
    if (parts.length === 1 && area(parts) > 4) {
      const cores = grow([parts[0]], -0.3).filter((p) => area([p]) > 0.3);
      if (cores.length > 1) {
        const pieces = cores.map((c) => intersection(grow([c], 0.31), [parts[0]])).filter((p) => p.length);
        const claimed = union(...pieces.map((p) => grow(p, 0.01)));
        const rest = difference([parts[0]], claimed);
        parts = pieces.map((p) => p[0]);
        // what the shrink left over (the pinch itself) goes to the largest piece
        if (area(rest) > 0.05) { const big = parts.reduce((b, p) => (area([p]) > area([b]) ? p : b)); const j = join([big], rest); if (j.length === 1) parts[parts.indexOf(big)] = j[0]; }
      }
    }
    parts.sort((a, b) => area([b]) - area([a]));
    if (parts.length === 0) continue;
    if (parts.length === 1) { out.push({ ...r, polygon: [parts[0]] }); continue; } // slivers under 0.3 m² are not rooms
    out.push({ kind: r.kind, polygon: [parts[0]] });
    for (const p of parts.slice(1)) {
      // a piece broken off the living room is the kitchen the flat lacks, else a store — never a bedroom
      const kind: RoomKind = r.kind !== "living" ? r.kind : u.rooms.some((x) => x.kind === "kitchen") || out.some((x) => x.kind === "kitchen") || area([p]) < 5 ? "storage" : "kitchen";
      out.push({ kind, polygon: [p] });
    }
  }
  u.rooms = out;
}

/**
 * The living room is wherever the light is: if it has no outside wall long
 * enough for a window, it swaps with the largest habitable room that has
 * one — a store big enough to live in counts, since leftovers are not
 * always small. Run again after every step that reshapes rooms.
 */
function litLiving(u: Unit, storey: MultiPoly): void {
  const lit = (r: Room) => facadeEdges(r.polygon, storey).some(([p, q]) => Math.hypot(q[0] - p[0], q[1] - p[1]) >= 1.3);
  const lv = u.rooms?.find((r) => r.kind === "living");
  if (!lv || lit(lv)) return;
  const other = u.rooms!.filter((r) => (r.kind === "bedroom" || r.kind === "kitchen" || (r.kind === "storage" && area(r.polygon) >= 9)) && lit(r)).sort((a, b) => area(b.polygon) - area(a.polygon))[0];
  if (other) { lv.kind = other.kind === "storage" && area(lv.polygon) >= 4 ? "kitchen" : other.kind; other.kind = "living"; }
}

/**
 * A room drawn the way bedrooms are: its short side at least 2.7 m and its
 * long side no more than twice that, measured in the frame of its longest
 * edge. `strict` 0 checks only the width (a living room may be long).
 */
function roomFits(mp: MultiPoly, strict = 1): boolean {
  const ring = mp[0]?.[0];
  if (!ring) return false;
  let best: [Pt, Pt] = [ring[0], ring[1]], L = 0;
  ring.forEach((a, i) => { const b = ring[(i + 1) % ring.length]; const l = Math.hypot(b[0] - a[0], b[1] - a[1]); if (l > L) { L = l; best = [a, b]; } });
  const ex = extent(frame(best[0], best[1]), mp);
  const w = Math.min(ex.s1 - ex.s0, ex.t1 - ex.t0), l = Math.max(ex.s1 - ex.s0, ex.t1 - ex.t0);
  // the width that counts is the room's own, not its bounding box: area over length for an L
  const eff = Math.min(w, area(mp) / l);
  return eff >= 2.7 && (!strict || l <= 2.05 * Math.max(w, eff));
}

/** Convex hull of a point set (monotone chain). */
function hull(pts: Pt[]): MultiPoly {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return [];
  const cross = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Pt[] = [], upper: Pt[] = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  for (const q of [...p].reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  return [[[...lower.slice(0, -1), ...upper.slice(0, -1)]]];
}
/** Width of a flat's hall: one person and a door swing. */
const HALL_WIDTH = 1.2;
/** A kitchen in the back zone, measured along the corridor. */
const KITCHEN_WIDTH = 2.6;

/** The rest of a service zone: into the living room if it opens onto it, else a store, else the hall. */
function leftover(piece: MultiPoly, rooms: Room[]): void {
  if (area(piece) < 0.3) return;
  const living = rooms.find((r) => r.kind === "living");
  const joined = living ? join(living.polygon, piece) : [];
  if (living && joined.length === 1) { living.polygon = joined; return; }
  if (area(piece) >= 1.5) { rooms.push({ kind: "storage", polygon: piece }); return; }
  const hall = rooms.find((r) => r.kind === "hall");
  if (hall && join(hall.polygon, piece).length === 1) hall.polygon = join(hall.polygon, piece);
}

interface Ctx { f: Frame; wing: number; prog: Programme; mix: MixCounter; sunAt: (at: Pt, normal: Pt) => number; shape?: MultiPoly; footprint?: MultiPoly }

/**
 * A corridor band filled with flats composed of bays (bays.ts). The run is
 * picked by the mix as before, then stretched or squeezed to end exactly at
 * the band's end; a flat that its stretched frontage cannot hold as its type
 * is composed as the next smaller type. Neighbours mirror, so their baths
 * sit back to back on one shaft and their living rooms side by side.
 */
function bayBand(c: Ctx, s0: number, s1: number, t0: number, t1: number, tCorridor: number, windows: "t0" | "t1", width: (m: MixEntry) => number): Cell[] {
  // (s0, s1 narrow to the full-depth part below)
  const { f, prog, mix } = c;
  const d = t1 - t0;
  // Whole flats go where the band is full depth; a slanting plot edge at
  // either end leaves a wedge, which the end flat takes into its end room,
  // the way end units are drawn. Clipping rectangles to the wedge instead
  // cut the end flats below their legal size and set off a chain of
  // re-declared types and merged neighbours.
  const S0 = s0, S1 = s1;
  if (c.shape) {
    const full = (s: number) => { const sl = rect(f, s, s + 0.2, t0, t1); return area(intersection(sl, c.shape!)) >= area(sl) * 0.98; };
    let a = s0, b = s1;
    while (a < s1 - 0.2 && !full(a)) a += 0.2;
    while (b > a + 0.2 && !full(b - 0.2)) b -= 0.2;
    if (b - a >= Math.min(...prog.mix.map(width)) * 0.9) { s0 = a; s1 = b; }
  }
  const len = s1 - s0;
  const picks: MixEntry[] = [];
  let used = 0;
  for (;;) {
    const m = mix.next((t) => used + width(prog.mix.find((x) => x.type === t)!) <= len + 1e-6);
    if (!m) break;
    picks.push(m); mix.take(m.type); used += width(m);
  }
  const smallest = [...prog.mix].sort((x, y) => width(x) - width(y))[0];
  if (!picks.length) { if (width(smallest) > len * 1.25) return []; picks.push(smallest); mix.take(smallest.type); used = width(smallest); }
  // the leftover goes to the flats in proportion, or one more small flat squeezes in, whichever moves them less
  let k = len / used;
  const squeezed = len / (used + width(smallest));
  // (squeezing must keep every flat drawable and above its type's legal area)
  const fitsAt = (m: MixEntry, kk: number) => width(m) * kk >= frontage(Number(m.type), prog.kitchen ?? "open").min && width(m) * kk * d * UNIT_NET >= MIN_DWELLING_AREA[m.type];
  if (Math.abs(1 - squeezed) < Math.abs(k - 1) && [...picks, smallest].every((m) => fitsAt(m, squeezed))) { picks.push(smallest); mix.take(smallest.type); k = squeezed; }

  const tWin = windows === "t0" ? t0 : t1, dir = windows === "t0" ? 1 : -1;
  const T = (t: number) => tWin + dir * t;
  const cells: Cell[] = [];
  let x = s0;
  picks.forEach((m, i) => {
    const w = width(m) * k;
    let type = Number(m.type);
    // the first flat's living room at the band's start, then pairs mirrored so baths share a shaft;
    // the last flat's at the band's end when that end is a facade (a gable, or the corner the band
    // runs through), so the end flat's living room has windows on both
    const outside = (s: number) => !!c.footprint && distanceTo(toWorld(f, s, (t0 + t1) / 2), c.footprint) > 0.2;
    const mirror = i === picks.length - 1 && i > 0 && outside(s1 + 0.4) ? false : i % 2 === 0;
    let b = composeSingle(type, w, d, { kitchen: prog.kitchen ?? "open", mirror });
    while (!b && type > 1) b = composeSingle(--type, w, d, { kitchen: prog.kitchen ?? "open", mirror });
    const x0 = x;
    x += w;
    if (!b) return;
    const R = (bx: [number, number, number, number]) => rect(f, x0 + bx[0], x0 + bx[1], T(bx[2]), T(bx[3]));
    const rooms: Room[] = b.rooms.map((r) => ({ kind: r.kind, polygon: join(...r.boxes.map(R)) }));
    const shape = rect(f, x0, x0 + w, tWin, T(d));
    const door = toWorld(f, x0 + b.door[0], tCorridor);
    const lm = x0 + (b.living[0] + b.living[1]) / 2;
    const wins: Window[] = [{ at: toWorld(f, lm, tWin), normal: dir === 1 ? neg(f.n) : f.n, living: true }];
    // a living room on a band end that is a facade has its second window there
    const endS = mirror ? x0 : x0 + w;
    if (outside(mirror ? x0 - 0.4 : x0 + w + 0.4) && (mirror ? b.living[0] < 0.1 : b.living[1] > w - 0.1)) wins.push({ at: toWorld(f, endS, T(Math.min(d, 4) / 2)), normal: mirror ? neg(f.u) : f.u, living: true });
    const [b0, b1] = b.balcony;
    const balcony = prog.balconyDepth > 0 && b1 - b0 > 0.8 ? rect(f, x0 + b0, x0 + b1, tWin, tWin - dir * prog.balconyDepth) : undefined;
    cells.push({ kind: "apartment", origin: "corridor", type: String(type) as UnitType, shape, wing: c.wing, door, balcony, rooms, windows: wins, frontage: w });
  });
  // The wedges at the ends: a small one goes to the end flat's end room; a
  // large one is an end flat of its own, composed on the wedge's length and
  // cut to it, its living room on the slanted end.
  const flats = cells.filter((x) => x.kind === "apartment");
  for (const [from, to, flat, atStart] of [[S0, s0, flats[0], true], [s1, S1, flats[flats.length - 1], false]] as const) {
    if (!flat || to - from < 0.1 || !c.shape) continue;
    const piece = intersection(rect(f, from, to, t0, t1), c.shape);
    if (area(piece) < 0.5) continue;
    if (area(piece) > 10) {
      const gross = area(piece);
      const type = (["4", "3", "2", "1"] as UnitType[]).find((t) => gross * UNIT_NET >= MIN_DWELLING_AREA[t] * 1.02 && to - from >= frontage(Number(t), prog.kitchen ?? "open").min);
      const b = type ? composeSingle(Number(type), to - from, d, { kitchen: prog.kitchen ?? "open", mirror: atStart }) : undefined;
      if (b) {
        const R = (bx: [number, number, number, number]) => intersection(rect(f, from + bx[0], from + bx[1], T(bx[2]), T(bx[3])), c.shape!);
        const rooms: Room[] = b.rooms.map((r) => ({ kind: r.kind, polygon: join(...r.boxes.map(R)) })).filter((r) => area(r.polygon) > 0.3);
        const lm = from + (b.living[0] + b.living[1]) / 2;
        cells.push({ kind: "apartment", origin: "corridor", type, shape: piece, wing: c.wing, door: toWorld(f, from + b.door[0], tCorridor), rooms, windows: [{ at: toWorld(f, lm, tWin), normal: dir === 1 ? neg(f.n) : f.n, living: true }], frontage: to - from });
        continue;
      }
    }
    const touch = (r: Room) => area(intersection(grow(r.polygon, 0.05), piece));
    const room = [...(flat.rooms ?? [])].sort((x, y) => touch(y) - touch(x))[0];
    if (!room || touch(room) < 0.01) continue;
    room.polygon = join(room.polygon, piece);
    flat.shape = join(flat.shape, piece);
  }
  return cells;
}

/**
 * Flats along one band of a wing, between s0 and s1, from t0 to t1.
 * `windows` says which facades of the band have windows: one for a band
 * against a corridor, both for a through wing.
 */
function fillBand(c: Ctx, s0: number, s1: number, t0: number, t1: number, access: Access, windows: "t0" | "t1" | "both"): Cell[] {
  const { f, prog, mix } = c;
  const d = t1 - t0;
  const len = s1 - s0;
  // Off a corridor a flat is composed of bays (bays.ts), so its frontage is
  // held to what its type can be drawn in: the mix area sets where in that
  // range it falls, not a 6 m living room.
  const bays = access.by === "corridor" && windows !== "both";
  const kitchenOf = prog.kitchen ?? "open";
  const width = (m: MixEntry) => {
    const w = m.area / UNIT_NET / d;
    if (!bays) return w;
    const r = frontage(Number(m.type), kitchenOf);
    // and no wider than a flat of the reference's p90 usable area at this depth
    const ref = REFERENCE[m.type] ?? REFERENCE["4"];
    const byArea = (ref.usable.p90 / UNIT_NET) / d;
    // never narrower than its type's legal minimum area needs, or the flat is re-declared a smaller type
    const legal = MIN_DWELLING_AREA[m.type] / UNIT_NET / d + 0.05;
    return Math.max(r.min, legal, Math.min(r.max, byArea, w));
  };
  const narrowest = Math.min(...prog.mix.map(width));
  if (len < narrowest * 0.6) return [];
  if (bays) return bayBand(c, s0, s1, t0, t1, access.t, windows as "t0" | "t1", width);

  let placed: { m: MixEntry; k: number }[];
  if (access.by === "landing") {
    const count = (access.left ? 1 : 0) + (access.right ? 1 : 0);
    if (count === 0) return [];
    placed = landingRun(len, d, count, prog, mix);
  } else {
    const picks: MixEntry[] = [];
    let used = 0;
    for (;;) {
      const m = mix.next((t) => used + width(prog.mix.find((x) => x.type === t)!) <= len + 1e-6);
      if (!m) break;
      picks.push(m); mix.take(m.type); used += width(m);
    }
    const smallest = [...prog.mix].sort((x, y) => x.area - y.area)[0];
    if (picks.length === 0) { picks.push(smallest); mix.take(smallest.type); used = width(smallest); }
    // The run must end exactly at the band's end. Either stretch what was
    // placed over the leftover, or add one more small flat and squeeze them
    // all, whichever moves the flats less from their target size, provided
    // squeezing keeps every flat above its legal minimum.
    let k = len / used;
    const squeezed = len / (used + width(smallest));
    const legal = [...picks, smallest].every((m) => m.area * squeezed >= MIN_DWELLING_AREA[m.type]);
    if (legal && Math.abs(1 - squeezed) < Math.abs(k - 1)) { picks.push(smallest); mix.take(smallest.type); k = squeezed; }
    placed = picks.map((m) => ({ m, k }));
  }

  const widths = placed.map(({ m, k }) => width(m) * k);
  const types = placed.map(({ m }) => m.type);

  // A through flat puts its living room on the facade with more sun; one
  // decision per run, so the flats of a run step against each other cleanly.
  const mid = (s0 + s1) / 2;
  let livingAt: "t0" | "t1" = windows === "t0" ? "t0" : "t1";
  if (windows === "both") livingAt = c.sunAt(toWorld(f, mid, t0), neg(f.n)) > c.sunAt(toWorld(f, mid, t1), f.n) ? "t0" : "t1";

  // A through flat is composed of bays on both facades (bays.ts): living
  // room on the sunnier one, bedrooms on the other, hall and bath between,
  // the door at the end where the landing is.
  if (windows === "both") {
    const tA = livingAt === "t0" ? t0 : t1, dirA = livingAt === "t0" ? 1 : -1;
    const T = (t: number) => tA + dirA * t;
    const cells: Cell[] = [];
    let x = s0;
    placed.forEach(({ m }, i) => {
      const w = widths[i];
      const x0 = x;
      x += w;
      const doorLeft = access.by === "landing" && i === 0 && access.left;
      let type = Number(m.type);
      const opt = { kitchen: prog.kitchen ?? "open", doorAtStart: doorLeft };
      let b = composeThrough(type, w, d, opt);
      while (!b && type > 2) b = composeThrough(--type, w, d, opt);
      if (!b) return;
      const R = (bx: [number, number, number, number]) => rect(f, x0 + bx[0], x0 + bx[1], T(bx[2]), T(bx[3]));
      const rooms: Room[] = b.rooms.map((r) => ({ kind: r.kind, polygon: join(...r.boxes.map(R)) }));
      const door = toWorld(f, x0 + b.door[0], access.t);
      const lm = x0 + (b.living[0] + b.living[1]) / 2;
      const wins: Window[] = [
        { at: toWorld(f, lm, tA), normal: dirA === 1 ? neg(f.n) : f.n, living: true },
        { at: toWorld(f, x0 + w / 2, T(d)), normal: dirA === 1 ? f.n : neg(f.n), living: false },
      ];
      const [b0, b1] = b.balcony;
      const balcony = prog.balconyDepth > 0 && b1 - b0 > 0.8 ? rect(f, x0 + b0, x0 + b1, tA, tA - dirA * prog.balconyDepth) : undefined;
      cells.push({ kind: "apartment", origin: "through", type: String(type) as UnitType, shape: rect(f, x0, x0 + w, t0, t1), wing: c.wing, door, balcony, rooms, windows: wins, frontage: 2 * w });
    });
    return cells;
  }

  return [];
}

/** The point of a flat's outline nearest its planned door that touches circulation, sampled every half metre. */
function doorOnto(u: Unit, circulation: MultiPoly): Pt | undefined {
  // a point on the hall's wall first: the front door opens into the hall (rooms.ts, rule 1)
  const halls = (u.rooms ?? []).filter((r) => r.kind === "hall");
  let best: Pt | undefined, bestD = Infinity, hallBest: Pt | undefined, hallD = Infinity;
  for (const poly of u.polygon) for (const ring of poly) ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    for (let k = 0.5; k < L - 0.5; k += 0.5) {
      const p: Pt = [a[0] + ((b[0] - a[0]) * k) / L, a[1] + ((b[1] - a[1]) * k) / L];
      if (distanceTo(p, circulation) > 0.1) continue;
      const d = u.door ? Math.hypot(p[0] - u.door[0], p[1] - u.door[1]) : 0;
      if (d < bestD) { bestD = d; best = p; }
      if (d < hallD && halls.some((h) => distanceToOutline(p, h.polygon) < 0.25)) { hallD = d; hallBest = p; }
    }
  });
  return hallBest ?? best;
}

/** First storey that holds flats, where shading is worst and the plan is decided. */
const firstResidential = (o: Option, prog: Programme) => o.storeys[Math.min(prog.groundUse === "commercial" ? 1 : 0, o.storeys.length - 1)];

interface Wing {
  w: number;
  edge: number;
  f: Frame;
  a: Pt;
  b: Pt;
  /** The wing as cut from the storey, before any corner is taken out of it. */
  full: MultiPoly;
  /** What its bands are laid out in: the wing minus the corner zones it owns. */
  shape: MultiPoly;
  t0: number;
  t1: number;
  tm: number;
  half: number; // half the corridor width, 0 for a through wing
  corridor: boolean;
}

const BIG = 1e4;
/** The point whose t is tO in frame O and tT in frame T: where two wing lines cross. */
function cross(O: Wing, tO: number, T: Wing, tT: number): Pt | undefined {
  const [ax, ay] = O.f.n, [bx, by] = T.f.n;
  const det = ax * by - ay * bx;
  if (Math.abs(det) < 1e-6) return undefined;
  const r1 = tO + ax * O.a[0] + ay * O.a[1], r2 = tT + bx * T.a[0] + by * T.a[1];
  return [(r1 * by - ay * r2) / det, (ax * r2 - bx * r1) / det];
}
/** The band of the plane between two t values of a wing's frame. */
const band = (W: Wing, lo: number, hi: number): MultiPoly => strip(W.a, W.b, lo, hi);

/** The flat type a given wall-to-wall area makes, capped at the largest the mix asks for. */
function typeFor(gross: number, prog: Programme): UnitType {
  const usable = gross * UNIT_NET;
  const allowed = prog.mix.map((m) => m.type).sort();
  const max = allowed[allowed.length - 1];
  const fit = (Object.keys(MIN_DWELLING_AREA) as UnitType[]).filter((t) => MIN_DWELLING_AREA[t] <= usable && t <= max).sort();
  return fit[fit.length - 1] ?? "1";
}

/**
 * The core in the inner corner of two corridor wings: the quadrant between
 * two facades that face each other at arm's length, which gets no useful
 * light, takes the stair, the lift and a landing on the corridor. Undefined
 * when the quadrant is too small for them.
 */
function cornerCore(O: Wing, T: Wing, C: MultiPoly, lifts: number, w: number): { core: Cell; Q4: MultiPoly } | undefined {
  const iO = O.tm + O.half, iT = T.tm + T.half; // inner edges of the corridors (or the mid-lines)
  const oO = O.tm - O.half, oT = T.tm - T.half; // outer edges
  const Q4 = intersection(C, intersection(band(O, iO, BIG), band(T, iT, BIG)));
  const armO = O.corridor ? intersection(C, intersection(band(O, oO, iO), band(T, oT, BIG))) : [];
  // the elbow square belongs to the owner's arm, so the two do not overlap
  const armT = T.corridor ? intersection(C, intersection(band(T, oT, iT), band(O, iO, BIG))) : [];
  const rest = difference(C, Q4, armO, armT);
  if (area(Q4) < 12 || area(rest) < 25) return undefined;

  // The core in the inner corner, laid out in the owner's frame: a landing
  // along the side the corner flat's door is on, the stair behind it, the
  // lift beside the stair. A quadrant too small for that is no place for a
  // core, and the corner is left to the wings as before.
  const eO = extent(O.f, Q4), eT = extent(T.f, Q4);
  const dO = eO.t1 - iO, dT = eT.t1 - iT;
  const LANDING = 1.6;
  const liftW = Math.min(LIFT.width, dO - LANDING - STAIR.width);
  if (liftW < 1.4 || dT < STAIR.length) return undefined;
  const landing = intersection(Q4, band(O, iO, iO + LANDING));
  const stair = intersection(Q4, intersection(band(O, iO + LANDING, iO + LANDING + STAIR.width), band(T, iT, iT + STAIR.length)));
  const liftParts: CorePart[] = [];
  for (let k = 0; k < lifts; k++) {
    const a0 = iT + k * (LIFT.depth + 0.1);
    if (a0 + LIFT.depth > eT.t1) break;
    liftParts.push({ kind: "lift", polygon: intersection(Q4, intersection(band(O, iO + LANDING + STAIR.width, iO + LANDING + STAIR.width + liftW), band(T, a0, a0 + LIFT.depth))) });
  }
  const parts: CorePart[] = [{ kind: "landing", polygon: landing }, { kind: "stair", polygon: stair }, ...liftParts];
  parts.push({ kind: "shaft", polygon: difference(Q4, union(...parts.map((p) => p.polygon))) });
  return { core: { kind: "core", shape: Q4, wing: w, parts, corner: true }, Q4 };
}

/**
 * One single-aspect flat composed of bays (bays.ts) in a rectangle of a
 * wing between its outer facade and `tDoor`, the side its door opens on (a
 * core landing): the largest type the mix allows that fits, its living room
 * at the end nearest `toward` (the corner square), with a window there too.
 */
function halfBandFlat(W: Wing, piece: MultiPoly, tDoor: number, toward: MultiPoly, prog: Programme, w: number): Cell | undefined {
  if (area(piece) < 20) return undefined;
  const { f } = W;
  const ex = extent(f, piece);
  const F = ex.s1 - ex.s0, D = Math.abs(tDoor - W.t0);
  const c = extent(f, toward);
  const atEnd = (c.s0 + c.s1) / 2 > (ex.s0 + ex.s1) / 2;
  const types = [...new Set(prog.mix.map((m) => Number(m.type)))].sort((a, b) => b - a);
  for (const type of types) {
    if (F * D * UNIT_NET < MIN_DWELLING_AREA[String(type) as UnitType]) continue;
    const b = composeSingle(type, F, D, { kitchen: prog.kitchen ?? "open", mirror: !atEnd });
    if (!b) continue;
    const T = (t: number) => W.t0 + t;
    const R = (bx: [number, number, number, number]) => intersection(rect(f, ex.s0 + bx[0], ex.s0 + bx[1], T(bx[2]), T(bx[3])), piece);
    const rooms: Room[] = b.rooms.map((r) => ({ kind: r.kind, polygon: join(...r.boxes.map(R)) })).filter((r) => area(r.polygon) > 0.3);
    const lm = ex.s0 + (b.living[0] + b.living[1]) / 2;
    const wins: Window[] = [{ at: toWorld(f, lm, W.t0), normal: neg(f.n), living: true }, { at: toWorld(f, atEnd ? ex.s1 : ex.s0, W.t0 + Math.min(D, 4) / 2), normal: atEnd ? f.u : neg(f.u), living: true }];
    const [b0, b1] = b.balcony;
    const balcony = prog.balconyDepth > 0 && b1 - b0 > 0.8 ? rect(f, ex.s0 + b0, ex.s0 + b1, W.t0, W.t0 - prog.balconyDepth) : undefined;
    return { kind: "apartment", origin: "corner", type: String(type) as UnitType, shape: piece, wing: w, door: toWorld(f, ex.s0 + b.door[0], tDoor), balcony, rooms, windows: wins, frontage: F + D };
  }
  return undefined;
}

/**
 * The corner of two through wings as one flat round the core in its inner
 * quadrant, in the earlier wing's frame (x from the far end to the corner,
 * t from its outer facade; a = half the depth):
 *
 *   - the living room in the corner square, a window on each facade;
 *   - on the other wing's facade, beside the living room, the kitchen and a
 *     bedroom, off a short hall that opens from the living room;
 *   - on this wing's facade, the entrance hall from the core landing, the
 *     bath beside it, and the master bedroom with its en-suite.
 *
 * The flat the reference draws at a block's corner: the living room takes
 * it, the kitchen sits next to it, bedrooms keep to single facades.
 */
function throughCornerFlat(O: Wing, C: MultiPoly, cornerSq: MultiPoly, prog: Programme, w: number): Cell | undefined {
  const { f } = O;
  const a = O.tm - O.t0;
  if (a < 5.6) return undefined;
  const exC = extent(f, C), exQ = extent(f, cornerSq);
  const atEnd = (exQ.s0 + exQ.s1) / 2 > (exC.s0 + exC.s1) / 2;
  const S = (x: number) => (atEnd ? exC.s1 - 2 * a + x : exC.s0 + 2 * a - x);
  const box = (x0: number, x1: number, t0: number, t1: number): MultiPoly => intersection(rect(f, Math.min(S(x0), S(x1)), Math.max(S(x0), S(x1)), O.t0 + t0, O.t0 + t1), C);
  const H = 1.25, hall = 1.4, bathW = 2.2, back = 2.6;
  const rooms: Room[] = [
    { kind: "living", polygon: box(a, 2 * a, 0, a) },
    { kind: "hall", polygon: box(a, a + H, a, 2 * a) },
    { kind: "kitchen", polygon: box(a + H, 2 * a, a, a + 2.4) },
    { kind: "bedroom", polygon: box(a + H, 2 * a, a + 2.4, 2 * a) },
    { kind: "hall", polygon: box(a - hall, a, a - back, a) },
    { kind: "bath", polygon: box(a - hall - bathW, a - hall, a - back, a) },
    { kind: "bedroom", polygon: box(0, a, 0, a - back) },
  ];
  if (a - hall - bathW >= 1.6) rooms.push({ kind: "bath", polygon: box(0, a - hall - bathW, a - back, a) });
  else if (a - hall - bathW > 0.3) rooms.push({ kind: "storage", polygon: box(0, a - hall - bathW, a - back, a) });
  const shape = join(...rooms.map((r) => r.polygon));
  const gross = area(shape);
  const type: UnitType = "3";
  if (gross * UNIT_NET < MIN_DWELLING_AREA[type]) return undefined;
  const corner = toWorld(f, S(1.5 * a), O.t0);
  const wins: Window[] = [{ at: corner, normal: neg(f.n), living: true }, { at: toWorld(f, S(2 * a), O.t0 + a / 2), normal: atEnd ? f.u : neg(f.u), living: true }];
  return { kind: "apartment", origin: "corner", type, shape, wing: w, door: toWorld(f, S(a - hall / 2), O.tm), rooms, windows: wins, frontage: 4 * a };
}

/**
 * The corner where two wings meet, cut into quadrants by their two
 * mid-lines. The inner quadrant sits between two facades that face each
 * other at close range and gets no useful light, so it takes the core
 * (stair, lift, an L-shaped landing along both sides). The outer quadrant
 * has two facades, and that is the corner flat, the one with the best
 * light in the building, its living room on the corner. The two side
 * quadrants carry on each wing's band, and corridors, where there are any,
 * turn the corner as an L. A piece too small to be a flat joins the corner flat.
 */
function cornerZone(O: Wing, T: Wing, C: MultiPoly, prog: Programme, lifts: number, w: number): Cell[] {
  const cells: Cell[] = [];
  const iO = O.tm + O.half, iT = T.tm + T.half; // inner edges of the corridors (or the mid-lines)
  const oO = O.tm - O.half, oT = T.tm - T.half; // outer edges
  const Q4 = intersection(C, intersection(band(O, iO, BIG), band(T, iT, BIG)));
  const armO = O.corridor ? intersection(C, intersection(band(O, oO, iO), band(T, oT, BIG))) : [];
  // the elbow square belongs to the owner's arm, so the two do not overlap
  const armT = T.corridor ? intersection(C, intersection(band(T, oT, iT), band(O, iO, BIG))) : [];
  const rest = difference(C, Q4, armO, armT);
  if (area(Q4) < 12 || area(rest) < 25) return [];

  // The core in the inner corner, laid out in the owner's frame: a landing
  // along the side the corner flat's door is on, the stair behind it, the
  // lift beside the stair. A quadrant too small for that is no place for a
  // core, and the corner is left to the wings as before.
  const eO = extent(O.f, Q4), eT = extent(T.f, Q4);
  const dO = eO.t1 - iO, dT = eT.t1 - iT;
  const LANDING = 1.6;
  const liftW = Math.min(LIFT.width, dO - LANDING - STAIR.width);
  if (liftW < 1.4 || dT < STAIR.length) return [];
  const landing = intersection(Q4, band(O, iO, iO + LANDING));
  const stair = intersection(Q4, intersection(band(O, iO + LANDING, iO + LANDING + STAIR.width), band(T, iT, iT + STAIR.length)));
  const liftParts: CorePart[] = [];
  for (let k = 0; k < lifts; k++) {
    const a0 = iT + k * (LIFT.depth + 0.1);
    if (a0 + LIFT.depth > eT.t1) break;
    liftParts.push({ kind: "lift", polygon: intersection(Q4, intersection(band(O, iO + LANDING + STAIR.width, iO + LANDING + STAIR.width + liftW), band(T, a0, a0 + LIFT.depth))) });
  }
  const parts: CorePart[] = [{ kind: "landing", polygon: landing }, { kind: "stair", polygon: stair }, ...liftParts];
  parts.push({ kind: "shaft", polygon: difference(Q4, union(...parts.map((p) => p.polygon))) });
  cells.push({ kind: "core", shape: Q4, wing: w, parts, corner: true });
  for (const arm of [armO, armT]) if (area(arm) > 1) cells.push({ kind: "corridor", shape: arm, wing: w });

  // the three outer pieces
  const DOOR_STRIP = 4.0;
  const Q1 = intersection(rest, intersection(band(O, -BIG, oO), band(T, -BIG, oT)));
  const sideO = intersection(rest, intersection(band(O, -BIG, oO), band(T, oT, BIG)));
  const sideT = intersection(rest, band(O, oO, BIG));
  const strip0 = intersection(sideO, band(T, oT, oT + DOOR_STRIP));
  let corner = join(Q1, strip0);
  let restO = difference(sideO, strip0);
  let restT = sideT;
  const tooSmall = (mp: MultiPoly) => area(mp) * UNIT_NET < MIN_DWELLING_AREA["1"];
  // A side piece opens onto its wing's corridor arm; with no corridor on
  // that side its door would face the core's stair, so it joins the corner flat.
  const mergedO = area(restO) > 0 && (tooSmall(restO) || !O.corridor);
  const mergedT = area(restT) > 0 && (tooSmall(restT) || !T.corridor);
  if (mergedO) { corner = join(corner, restO); }
  if (mergedT) { corner = join(corner, restT); }
  if (mergedO) restO = [];
  if (mergedT) restT = [];

  const at = (tO: number, tT: number) => cross(O, tO, T, tT);
  // window wall: the part of a piece's outline on either outer facade
  const facadeLength = (mp: MultiPoly) => {
    let len = 0;
    for (const poly of mp) for (const ring of poly) ring.forEach((p, i) => {
      const q = ring[(i + 1) % ring.length];
      for (const W of [O, T]) {
        const tp = toLocal(W.f, p)[1], tq = toLocal(W.f, q)[1];
        if (Math.abs(tp - W.t0) < 0.05 && Math.abs(tq - W.t0) < 0.05) len += Math.hypot(q[0] - p[0], q[1] - p[1]);
      }
    });
    return len;
  };
  const flat = (shape: MultiPoly, door: Pt | undefined, wins: Window[], rooms: Room[]) => {
    if (!door || area(shape) < 20) return;
    cells.push({ kind: "apartment", origin: "corner", type: typeFor(area(shape), prog), shape, wing: w, door, windows: wins, rooms, frontage: facadeLength(shape) });
  };

  // corner flat: living room on the corner; hall and bath where the door is; bedrooms in what it absorbed
  {
    const service = intersection(strip0, band(O, oO - SERVICE_DEPTH, oO));
    const bath = intersection(service, band(T, oT, oT + BATH_WIDTH));
    const hall = difference(service, bath);
    const living = difference(join(Q1, strip0), service);
    const rooms: Room[] = [{ kind: "living", polygon: living }, { kind: "bath", polygon: bath }, { kind: "hall", polygon: hall }];
    if (mergedO) rooms.push({ kind: "bedroom", polygon: restOrNone(sideO, strip0) });
    if (mergedT) rooms.push({ kind: "bedroom", polygon: sideT });
    const d = onLine(corner, O, oO, at(oO, oT + BATH_WIDTH + (DOOR_STRIP - BATH_WIDTH) / 2));
    const wO = at(O.t0, (T.t0 + oT) / 2), wT = at((O.t0 + oO) / 2, T.t0);
    const wins: Window[] = [];
    // the living room has both corner facades; the sun is counted on the better one
    if (wO) wins.push({ at: wO, normal: neg(O.f.n), living: true });
    if (wT) wins.push({ at: wT, normal: neg(T.f.n), living: true });
    flat(corner, d, wins, rooms.filter((r) => area(r.polygon) > 0.3));
  }
  // the side pieces: single-aspect flats on their own wing's outer facade
  const side = (shape: MultiPoly, W: Wing, Other: Wing, tDoorOwn: number, tOther: (r: [number, number]) => number) => {
    if (!area(shape)) return;
    const ex = extent(Other.f, shape); // its span across the other wing's frame
    const mid = tOther([ex.t0, ex.t1]);
    const d = onLine(shape, W, tDoorOwn, W === O ? at(tDoorOwn, mid) : at(mid, tDoorOwn));
    const w0 = W === O ? at(O.t0, mid) : at(mid, T.t0);
    const service = intersection(shape, band(W, (W === O ? oO : oT) - SERVICE_DEPTH, BIG));
    const bath = intersection(service, band(Other, ex.t1 - BATH_WIDTH, BIG));
    const rooms: Room[] = [{ kind: "living", polygon: difference(shape, service) }, { kind: "bath", polygon: bath }, { kind: "hall", polygon: difference(service, bath) }];
    flat(shape, d, w0 ? [{ at: w0, normal: neg(W.f.n), living: true }] : [], rooms.filter((r) => area(r.polygon) > 0.3));
  };
  side(restO, O, T, oO, (r) => (r[0] + r[1]) / 2);
  side(restT, T, O, oT, (r) => (r[0] + r[1]) / 2);
  return cells;
}
const restOrNone = (a: MultiPoly, b: MultiPoly) => difference(a, b);

/**
 * The point of a shape's outline that lies on the line t = `t` of a wing's
 * frame and is nearest to `near`: where a door into that piece can actually
 * be, rather than where the middle of its bounding box happens to fall.
 */
function onLine(shape: MultiPoly, W: Wing, t: number, near: Pt | undefined): Pt | undefined {
  let best: Pt | undefined, bestD = Infinity;
  for (const poly of shape) for (const ring of poly) ring.forEach((p, i) => {
    const q = ring[(i + 1) % ring.length];
    if (Math.abs(toLocal(W.f, p)[1] - t) > 0.05 || Math.abs(toLocal(W.f, q)[1] - t) > 0.05) return;
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (len < 1.0) return; // too short for a door
    // keep half a metre clear of either end, then clamp the target onto it
    const ux = (q[0] - p[0]) / len, uy = (q[1] - p[1]) / len;
    const target = near ?? [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    const k = Math.max(0.5, Math.min(len - 0.5, (target[0] - p[0]) * ux + (target[1] - p[1]) * uy));
    const c: Pt = [p[0] + ux * k, p[1] + uy * k];
    const d = Math.hypot(c[0] - target[0], c[1] - target[1]);
    if (d < bestD) { bestD = d; best = c; }
  });
  return best;
}

/**
 * A point block: the core in the middle (stair and lifts in a block, a
 * landing all round it), and one flat in each quadrant, wrapped round its
 * corner with windows on both facades. On the ground floor a lobby runs
 * from the core to the street facade; above, the flats take it back.
 */
function towerPlan(env: Envelope, o: Option, prog: Programme, lifts: number): Cell[] {
  const ref = o.storeys[Math.min(1, o.storeys.length - 1)].footprint;
  const e = edges(env.plot[0][0]);
  const [a, b] = e.reduce((best, x) => (Math.hypot(x[1][0] - x[0][0], x[1][1] - x[0][1]) > Math.hypot(best[1][0] - best[0][0], best[1][1] - best[0][1]) ? x : best));
  const f = frame(a, b);
  const { s0, s1, t0, t1 } = extent(f, ref);
  const cs = (s0 + s1) / 2, ct = (t0 + t1) / 2;
  const [bw, bh, ring] = [2.4, 2.8, 1.6]; // half sizes of the stair-and-lift block, landing width
  const block = rect(f, cs - bw, cs + bw, ct - bh, ct + bh);
  const outer = rect(f, cs - bw - ring, cs + bw + ring, ct - bh - ring, ct + bh + ring);
  const parts: CorePart[] = [
    { kind: "landing", polygon: difference(outer, block) },
    { kind: "stair", polygon: rect(f, cs - bw, cs - bw + STAIR.width, ct - bh, ct + bh) },
  ];
  for (let k = 0; k < lifts; k++) parts.push({ kind: "lift", polygon: rect(f, cs + bw - LIFT.width, cs + bw, ct - bh + k * (LIFT.depth + 0.1), ct - bh + k * (LIFT.depth + 0.1) + LIFT.depth) });
  parts.push({ kind: "shaft", polygon: difference(block, union(...parts.slice(1).map((p) => p.polygon))) });
  // the lobby, ground floor only, from the landing to the street facade
  const lobby = rect(f, cs - 1.2, cs + 1.2, t0, ct - bh - ring);
  const cells: Cell[] = [{
    kind: "core", shape: join(outer, lobby), wing: 0, entrance: toWorld(f, cs, t0),
    parts: [...parts.slice(0, -1), { kind: "shaft", polygon: join(parts[parts.length - 1].polygon, lobby) }],
    upper: { shape: outer, parts },
  }];
  const quadrants: [number, number][] = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  for (const [ds, dt] of quadrants) {
    const q = rect(f, ds > 0 ? cs : s0 - 1, ds > 0 ? s1 + 1 : cs, dt > 0 ? ct : t0 - 1, dt > 0 ? t1 + 1 : ct);
    const full = difference(intersection(ref, q), outer);
    const shape = difference(full, lobby);
    const extra = intersection(full, lobby);
    if (area(shape) < 20) continue;
    // living room in the corner, a service strip along the landing, bedrooms in between
    const sF = ds > 0 ? s1 : s0, tF = dt > 0 ? t1 : t0;
    const living = intersection(shape, rect(f, Math.min(sF, sF - ds * (s1 - s0) * 0.28), Math.max(sF, sF - ds * (s1 - s0) * 0.28), Math.min(tF, tF - dt * (t1 - t0) * 0.28), Math.max(tF, tF - dt * (t1 - t0) * 0.28)));
    // a hall one person wide round the core, and the bath in its corner, diagonal to the core, off both arms
    const hw = HALL_WIDTH + 0.2;
    const service = intersection(shape, rect(f, cs - bw - ring - hw, cs + bw + ring + hw, ct - bh - ring - hw, ct + bh + ring + hw));
    // the bath at the far end of the arm along the core's other face, not in the hall's elbow
    // (there it split the hall into two arms that met only at a corner)
    const es = cs + ds * (bw + ring + hw), et = ct + dt * (bh + ring);
    const tFar = ct + dt * (bh + ring + hw);
    const bath = difference(intersection(shape, rect(f, Math.min(es, es + ds * 2.4), Math.max(es, es + ds * 2.4), Math.min(ct, tFar), Math.max(ct, tFar))), living);
    const rest = difference(shape, living, service, bath);
    const rooms: Room[] = [{ kind: "living", polygon: living }, { kind: "hall", polygon: difference(service, living, bath) }, { kind: "bath", polygon: bath }, ...rest.map((p) => ({ kind: "bedroom" as const, polygon: [p] }))];
    const door = toWorld(f, cs + ds * (bw + ring), ct + dt * (bh / 2));
    const windows: Window[] = [
      { at: toWorld(f, (cs + sF) / 2, tF), normal: dt > 0 ? f.n : neg(f.n), living: true },
      { at: toWorld(f, sF, (ct + tF) / 2), normal: ds > 0 ? f.u : neg(f.u), living: true },
    ];
    const gross = area(shape) + area(extra);
    cells.push({
      kind: "apartment", origin: "tower", type: typeFor(gross, prog), shape, wing: 0, door, windows, frontage: (s1 - s0) / 2 + (t1 - t0) / 2,
      rooms: rooms.filter((r) => area(r.polygon) > 0.5), extra: area(extra) > 0.5 ? extra : undefined,
    });
  }
  return cells;
}

/** The typical plan of every wing, cut from the first storey of the main body. */
function typicalPlan(env: Envelope, o: Option, base: Programme, sun: SunSample[], masses: Mass[], mixOverride?: Programme["mix"]): Cell[] {
  // Cores, corridors and the decision to split a run come from the main mix,
  // so every storey range puts its cores in the same place; only the flats
  // between them follow the range's own mix.
  const prog: Programme = mixOverride ? { ...base, mix: mixOverride } : base;
  if (o.typology === "tower") return towerPlan(env, o, prog, o.storeys.length - 1 > SECOND_LIFT_ABOVE ? 2 : 1);
  const e = edges(env.plot[0][0]);
  const ref = o.storeys[Math.min(1, o.storeys.length - 1)].footprint;
  const z = firstResidential(o, prog).base + 1.5;
  const sunAt = (at: Pt, normal: Pt) => sunHours(at, z, normal, masses, sun);
  const mix = new MixCounter(prog.mix);
  const cells: Cell[] = [];
  const lifts = o.storeys.length - 1 > SECOND_LIFT_ABOVE ? 2 : 1;

  // 1. the wings, each excluding the ones before it
  let taken: MultiPoly = [];
  const wings: (Wing | undefined)[] = o.along.map((edge, w) => {
    const [a, b] = e[edge];
    const f = frame(a, b);
    const full = difference(intersection(ref, strip(a, b, 0, o.reach[w])), taken);
    taken = union(taken, full);
    if (area(full) < 20) return undefined;
    const { t0, t1 } = extent(f, full);
    const corridor = t1 - t0 >= CORRIDOR_FROM;
    return { w, edge, f, a, b, full, shape: full, t0, t1, tm: (t0 + t1) / 2, half: corridor ? CORRIDOR_WIDTH / 2 : 0, corridor };
  });

  // 2. corners: every junction of consecutive wings (and the closing one of a courtyard block)
  const pairs: [number, number][] = [];
  for (let k = 0; k + 1 < wings.length; k++) pairs.push([k, k + 1]);
  if (o.typology === "perimeter" && wings.length > 2) pairs.push([wings.length - 1, 0]);
  for (const [x, y] of pairs) {
    const [O, T] = x < y ? [wings[x], wings[y]] : [wings[y], wings[x]]; // the earlier wing owns the corner
    if (!O || !T) continue;
    // Only between two real wings: on a concave plot a wing's band can reach
    // across into the plot's other arm, and its measured depth stops being
    // the wing's depth. A corner laid out on that would invent rooms the
    // size of a car park, so such a corner stays as the wings left it.
    if (Math.abs(O.t1 - O.t0 - o.depth) > 1 || Math.abs(T.t1 - T.t0 - o.depth) > 1) continue;
    const C = intersection(O.shape, band(T, -BIG, T.t1));
    if (area(C) < 40) continue;
    // Two corridor wings: the inner quadrant is the core, the corridors meet
    // at the corner's middle, the earlier wing's outer band runs on through
    // the corner to the other facade (its last flat is the corner flat, its
    // living room on both facades) and the later wing's outer band starts
    // just past the corridor. Every flat is then composed of bays like any
    // other, which is how the reference corners are drawn.
    if (O.corridor && T.corridor) {
      const cc = cornerCore(O, T, C, lifts, O.w);
      if (cc) {
        const iO = O.tm + O.half, iT = T.tm + T.half;
        const inner = intersection(C, band(O, iO, BIG));
        cells.push(cc.core);
        O.shape = difference(O.shape, inner);
        T.shape = union(T.shape, difference(intersection(inner, band(T, -BIG, iT)), cc.Q4));
        continue;
      }
    }
    // Two through wings: the inner quadrant is the core too, and the corner
    // is two single-aspect flats opening off its landing: the earlier wing's
    // outer half (the corner square and the one beside it, its living room
    // on the corner) and the later wing's outer quadrant.
    if (!O.corridor && !T.corridor) {
      const cc = cornerCore(O, T, C, lifts, O.w);
      if (cc) {
        const outerO = difference(intersection(C, band(O, -BIG, O.tm)), cc.Q4);
        const outerT = difference(intersection(C, intersection(band(O, O.tm, BIG), band(T, -BIG, T.tm))), cc.Q4);
        const cornerSq = intersection(C, intersection(band(O, -BIG, O.tm), band(T, -BIG, T.tm)));
        const flats = [halfBandFlat(O, outerO, O.tm, cornerSq, prog, O.w), halfBandFlat(T, outerT, T.tm, cornerSq, prog, O.w)];
        // the later wing's quadrant is too small for a flat of its own: one corner flat wraps the core
        const one = flats.every(Boolean) ? undefined : throughCornerFlat(O, C, cornerSq, prog, O.w);
        if (flats.every(Boolean) || one) {
          cells.push(cc.core, ...(one ? [one] : (flats as Cell[])));
          O.shape = difference(O.shape, C);
          continue;
        }
      }
    }
    const zone = cornerZone(O, T, C, prog, lifts, O.w);
    if (!zone.length) continue;
    cells.push(...zone);
    O.shape = difference(O.shape, C);
  }

  // 3. each wing's bands, in what is left of it
  for (const W of wings) {
    if (!W || area(W.shape) < 20) continue;
    const { f, w, t0, t1, tm, corridor } = W;
    const wingShape = W.shape;
    // Cells are cut as rectangles in the wing's frame, then clipped to the
    // wing, so a corner shared by two wings belongs to exactly one of them.
    const first = cells.length;
    const { s0, s1 } = extent(f, wingShape);
    const len = s1 - s0;
    const half = CORRIDOR_WIDTH / 2;
    // A corridor wing puts its cores on the band that gets less sun, so the
    // flats get the sunny side; on a wing running east–west that keeps the
    // cores, not the flats, on the north facade.
    const mid = (s0 + s1) / 2;
    const outerSun = sunAt(toWorld(f, mid, t0), neg(f.n)), innerSun = sunAt(toWorld(f, mid, t1), f.n);
    const coresOuter = outerSun <= innerSun;
    const coreBand: [number, number] = !corridor ? [t0, t1] : coresOuter ? [t0, tm - half] : [tm + half, t1];
    // Without a corridor every flat opens off a core landing, so a core
    // serves FLATS_PER_CORE flats a floor and the pitch follows the flat
    // width. With one, only escape distance spaces them. (A corner core
    // serves only the corner's own flats: the landing of a through wing's
    // core cannot reach round its stair to the wing's end flat.)
    const avgWidth = base.mix.reduce((t, m) => t + m.share * m.area, 0) / UNIT_NET / (t1 - t0);
    const pitch = corridor ? CORE_SPACING : FLATS_PER_CORE * avgWidth + CORE_WIDTH;
    // Through wing: the count that stretches its flats least, the end runs
    // holding one flat and the runs between cores two.
    let cores = Math.max(1, Math.round(len / pitch));
    if (!corridor) {
      const stretch = (n: number) => {
        const inner = len / n - CORE_WIDTH, end = inner / 2;
        return Math.max(Math.abs(Math.log(Math.max(end, 0.1) / avgWidth)), n > 1 ? Math.abs(Math.log(Math.max(inner, 0.1) / (2 * avgWidth))) : 0);
      };
      for (let n = 1; n <= Math.ceil(len / pitch) + 1; n++) if (stretch(n) < stretch(cores) - 1e-9) cores = n;
    }
    // A core must stand wholly inside its wing: at a wing's tapering end or
    // in a corner a nominal position can leave half the stair outside the
    // building. Slide it inward a metre at a time until it fits, or drop it.
    const inside = (c: number) => area(difference(rect(f, c - CORE_WIDTH / 2, c + CORE_WIDTH / 2, coreBand[0], coreBand[1]), wingShape)) < 0.5;
    const centres = Array.from({ length: cores }, (_, j) => s0 + (len * (j + 0.5)) / cores)
      .map((c) => {
        for (let k = 0; k <= Math.ceil(pitch / 2); k++) for (const sign of [1, -1]) {
          const at = c + sign * k * (c < mid ? 1 : -1);
          if (at - CORE_WIDTH / 2 >= s0 && at + CORE_WIDTH / 2 <= s1 && inside(at)) return at;
        }
        return NaN;
      })
      .filter((c) => Number.isFinite(c))
      .filter((c, i, all) => i === 0 || c - all[i - 1] >= CORE_WIDTH);

    if (corridor) cells.push({ kind: "corridor", shape: rect(f, s0, s1, tm - half, tm + half), wing: w });
    const cw = CORE_WIDTH / 2;
    for (const c of centres) {
      if (corridor) {
        // landing against the corridor, stair and lifts back towards the facade, entrance there
        const landing: [number, number] = coresOuter ? [tm - half - 1.6, tm - half] : [tm + half, tm + half + 1.6];
        const { parts, entrance } = coreParts(f, c, coreBand[0], coreBand[1], landing, coresOuter ? -1 : 1, lifts, coresOuter ? t0 : t1);
        cells.push({ kind: "core", shape: rect(f, c - cw, c + cw, coreBand[0], coreBand[1]), wing: w, entrance, parts });
      } else {
        // Through wing: on the ground floor the core runs facade to facade,
        // because the entrance hall needs it; above, only landing, stair and
        // lifts are core, and the flats either side take what is left.
        const landing: [number, number] = [tm - 1.3, tm + 1.3];
        const { parts, entrance } = coreParts(f, c, t0, t1, landing, 1, lifts, t0);
        const top = Math.min(t1, tm + 1.3 + STAIR.length);
        const upperShape = rect(f, c - cw, c + cw, landing[0], top);
        const upperParts = coreParts(f, c, landing[0], top, landing, 1, lifts, t0).parts;
        cells.push({ kind: "core", shape: rect(f, c - cw, c + cw, t0, t1), wing: w, entrance, parts, upper: { shape: upperShape, parts: upperParts } });
      }
    }
    const runs: [number, number][] = [];
    const cuts = [s0, ...centres.flatMap((c) => [c - cw, c + cw]), s1];
    for (let j = 0; j < cuts.length; j += 2) runs.push([cuts[j], cuts[j + 1]]);
    const ctx: Ctx = { f, wing: w, prog, mix, sunAt, shape: wingShape, footprint: ref };
    if (corridor) {
      // the core band is split by the cores, the other band runs whole; both open onto the corridor
      const outer: [number, number] = [t0, tm - half], inner: [number, number] = [tm + half, t1];
      const [split, whole] = coresOuter ? [outer, inner] : [inner, outer];
      const winSplit = coresOuter ? "t0" : "t1", winWhole = coresOuter ? "t1" : "t0";
      const tSplit = coresOuter ? tm - half : tm + half, tWhole = coresOuter ? tm + half : tm - half;
      runs.forEach(([r0, r1]) => cells.push(...fillBand(ctx, r0, r1, split[0], split[1], { by: "corridor", t: tSplit }, winSplit)));
      cells.push(...fillBand(ctx, s0, s1, whole[0], whole[1], { by: "corridor", t: tWhole }, winWhole));
    } else {
      runs.forEach(([r0, r1], j) => {
        const left = j > 0, right = j < runs.length - 1;
        const count = (left ? 1 : 0) + (right ? 1 : 0);
        // one through flat per core, its living room on the sunnier facade (fillBand)
        cells.push(...fillBand(ctx, r0, r1, t0, t1, { by: "landing", left, right, t: tm }, "both"));
      });
      // give each core's leftover ends to the flats beside it, above the ground floor
      // (the street-side end to the flat on the left, the far end to the one on the right)
      const sOf = (mp: MultiPoly) => { const pts = mp.flatMap((p) => p[0]).map((p) => toLocal(f, p)); return { s: pts.reduce((t, p) => t + p[0], 0) / pts.length, t: pts.reduce((t, p) => t + p[1], 0) / pts.length }; };
      for (const core of cells.slice(first).filter((x) => x.kind === "core" && x.upper)) {
        const cs = sOf(core.shape).s;
        for (const piece of difference(core.shape, core.upper!.shape)) {
          const low = sOf([piece]).t < tm;
          const touching = cells.slice(first).filter((x) => x.kind === "apartment" && join(x.shape, [piece]).length === 1);
          const flat = touching.find((x) => (sOf(x.shape).s < cs) === low) ?? touching[0];
          // only while the flat stays near its target size; otherwise the
          // piece stays in the core as storage rooms for the flats (boxe)
          const target = prog.mix.find((m) => m.type === flat?.type)?.area ?? 0;
          if (flat && (area(flat.shape) + area(flat.extra ?? []) + area([piece])) * UNIT_NET <= target * 1.4) flat.extra = flat.extra ? join(flat.extra, [piece]) : [piece];
          else if (core.upper) core.upper = { shape: join(core.upper.shape, [piece]), parts: [...core.upper.parts.filter((p) => p.kind !== "shaft"), { kind: "shaft", polygon: join(...core.upper.parts.filter((p) => p.kind === "shaft").map((p) => p.polygon), [piece]) }] };
        }
      }
    }
    for (const c of cells.slice(first)) {
      c.shape = intersection(c.shape, wingShape);
      c.parts = c.parts?.map((p) => ({ ...p, polygon: intersection(p.polygon, wingShape) }));
      if (c.upper) c.upper = { shape: intersection(c.upper.shape, wingShape), parts: c.upper.parts.map((p) => ({ ...p, polygon: intersection(p.polygon, wingShape) })) };
      if (c.extra) c.extra = intersection(c.extra, wingShape);
      c.rooms = c.rooms?.map((r) => ({ ...r, polygon: intersection(r.polygon, wingShape) })).filter((r) => area(r.polygon) > 0.3);
      // clipping can cut away the corner the door was planned in: move it onto the wall that is left
      if (c.door && c.shape.length && distanceTo(c.door, c.shape) > 0.05) c.door = nearestOnOutline(c.door, c.shape);
    }
  }
  return cells;
}

/**
 * A podium storey: parking inside the building, around the cores that carry
 * on up to the flats. With more than one podium level a ramp runs along the
 * first wing's street end; the ground level is entered from the street.
 * Under Legea 169/2026 the area counts in CUT — only basement and
 * demi-basement parking is deducted — which is the trade-off the report states.
 */
function podiumFloor(env: Envelope, o: Option, plan: Cell[], s: Option["storeys"][number], levels: number, above: boolean): Unit[] {
  const units: Unit[] = [];
  let n = 0;
  for (const c of plan.filter((x) => x.kind === "core")) {
    const own = above && c.upper ? c.upper.shape : c.shape;
    const shape = intersection(own, s.footprint);
    if (area(shape) < 1) continue;
    const parts = above && c.upper ? c.upper.parts : c.parts;
    units.push({
      id: `${s.index}.${++n}`, kind: "core", storey: s.index, polygon: shape, area: area(shape),
      entrance: s.index === 0 ? c.entrance : undefined, corner: c.corner,
      parts: parts?.map((p) => ({ ...p, polygon: intersection(p.polygon, s.footprint) })).filter((p) => area(p.polygon) > 0.1),
    });
  }
  const cores = union(...units.map((u) => u.polygon));
  // rows along the first wing, or along the longest plot edge for a tower
  const pe = edges(env.plot[0][0]);
  const [a, b] = o.along.length ? pe[o.along[0]] : pe.reduce((m, x) => (Math.hypot(x[1][0] - x[0][0], x[1][1] - x[0][1]) > Math.hypot(m[1][0] - m[0][0], m[1][1] - m[0][1]) ? x : m));
  const f = frame(a, b);
  const ex = extent(f, s.footprint);
  const ramp = levels > 1 ? intersection(rect(f, ex.s1 - 20.5, ex.s1 - 0.5, ex.t0 + 0.5, ex.t0 + 6.5), s.footprint) : [];
  const region = difference(s.footprint, cores);
  const { stalls, aisles } = packStalls(region, ramp, a, b);
  units.push({ id: `${s.index}.${++n}`, kind: "parking", storey: s.index, polygon: region, area: area(region), stalls, aisles, ramp });
  return units;
}

export function layout(env: Envelope, o: Option, prog: Programme, latitude = DEFAULT_LATITUDE, context: Mass[] = []): Layout {
  const sun = solsticeSun(latitude);
  // our storeys shade our flats, and so do the neighbours when they are known
  const masses: Mass[] = [...o.storeys.map((s) => ({ footprint: s.footprint, base: s.base, top: s.cornice })), ...context];
  const plan = typicalPlan(env, o, prog, sun, masses);
  const ranges = (prog.mixByLevel ?? []).filter((r) => r.mix.some((m) => m.share > 0)).map((r) => ({ ...r, plan: typicalPlan(env, o, prog, sun, masses, r.mix) }));
  const planFor = (i: number) => ranges.find((r) => i >= r.from && i <= r.to)?.plan ?? plan;
  const podium = Math.min(prog.parking?.podiumLevels ?? 0, Math.max(0, o.storeys.length - 1));
  const smallest = Math.min(...prog.mix.map((m) => m.area)) / UNIT_NET;
  const clip = (mp: MultiPoly, to: MultiPoly) => intersection(mp, to);
  const floors: FloorPlan[] = o.storeys.map((s) => {
    const units: Unit[] = [];
    const retail = s.index === 0 && prog.groundUse === "commercial";
    const above = s.index > 0;
    let n = 0;
    if (s.index < podium) return { index: s.index, units: podiumFloor(env, o, plan, s, podium, above) };
    for (const c of planFor(s.index)) {
      const own = c.kind === "core" && above && c.upper ? c.upper.shape : c.kind === "apartment" && above && c.extra ? join(c.shape, c.extra) : c.shape;
      let shape = clip(own, s.footprint);
      // A flat is one piece. A setback storey can cut the waist of a stepped
      // flat and leave its halves apart; keep the larger, whose door the check
      // below moves onto its own wall, and the rest is terrace — not a room
      // nobody can walk into.
      if (c.kind === "apartment" && shape.length > 1) shape = [shape.reduce((best, p) => (area([p]) > area([best]) ? p : best))];
      const a = area(shape);
      if (a < 1) continue;
      const id = `${s.index}.${++n}`;
      if (c.kind !== "apartment") {
        const parts = c.kind === "core" && above && c.upper ? c.upper.parts : c.parts;
        units.push({
          id, kind: c.kind, storey: s.index, polygon: shape, area: a,
          entrance: s.index === 0 && c.kind === "core" ? c.entrance : undefined, corner: c.corner,
          parts: parts?.map((p) => ({ ...p, polygon: clip(p.polygon, s.footprint) })).filter((p) => area(p.polygon) > 0.1),
        });
        continue;
      }
      if (retail) { units.push({ id, kind: "retail", storey: s.index, polygon: shape, area: a }); continue; }
      // A sliver left by a setback storey is not a flat: give it to the
      // neighbour in the same row if there is one, else leave it as terrace.
      if (a < smallest * 0.6) {
        const prev = units[units.length - 1];
        const joined = prev?.kind === "apartment" ? join(prev.polygon, shape) : [];
        if (joined.length === 1) { prev.polygon = joined; prev.area = area(prev.polygon); }
        continue;
      }
      // A balcony may not leave the envelope: setbacks are measured to the
      // outermost element here, the conservative reading. It hangs outside
      // the facade, so the storey's own footprint is cut out of it.
      const env_s = env.storeys[s.index]?.footprint ?? [];
      const balcony = c.balcony ? difference(intersection(c.balcony, env_s), s.footprint) : undefined;
      const rooms = [...(c.rooms ?? []), ...(above && c.extra ? [{ kind: "storage" as const, polygon: c.extra }] : [])]
        .map((r) => ({ ...r, polygon: clip(r.polygon, shape) }))
        .filter((r) => area(r.polygon) > 0.3);
      // sun on the living-room window, at this storey's height; a window
      // whose wall a setback storey removed is not a window
      const z = s.base + 1.5;
      const live = (c.windows ?? []).filter((w) => w.living && distanceTo(w.at, s.footprint) < 0.2);
      const hours = live.length ? Math.max(...live.map((w) => sunHours(w.at, z, w.normal, masses, sun))) : 0;
      units.push({
        id, kind: "apartment", origin: c.origin, type: c.type, storey: s.index, polygon: shape, area: a, door: c.door,
        balcony: balcony && area(balcony) >= 1 ? balcony : undefined, rooms, windows: c.windows, frontage: c.frontage, sunHours: hours,
      });
    }
    // A front door opens onto a corridor or a landing, not onto a stair or a
    // lift shaft. One that does not (a setback took the core, a corner cut
    // the wall it was planned in) moves along the flat's own outline to the
    // nearest point that does; a flat with no such point cannot be reached
    // and is not a flat.
    const circulation = union(
      ...units.filter((u) => u.kind === "corridor").map((u) => u.polygon),
      ...units.flatMap((u) => u.parts ?? []).filter((p) => p.kind === "landing").map((p) => p.polygon),
    );
    for (const u of units.filter((x) => x.kind === "apartment")) {
      const intoHall = !u.rooms?.some((r) => r.kind === "hall") || u.rooms.some((r) => r.kind === "hall" && u.door && distanceToOutline(u.door, r.polygon) < 0.3);
      if (u.door && intoHall && distanceTo(u.door, circulation) <= DOOR_TOLERANCE && distanceToOutline(u.door, u.polygon) <= DOOR_TOLERANCE) continue;
      const moved = doorOnto(u, circulation);
      // a door that was fine but opened into a room, with no hall wall on circulation to move to, stays
      if (moved && !intoHall && u.door && !(u.rooms ?? []).some((r) => r.kind === "hall" && distanceToOutline(moved, r.polygon) < 0.25) && distanceTo(u.door, circulation) <= DOOR_TOLERANCE && distanceToOutline(u.door, u.polygon) <= DOOR_TOLERANCE) continue;
      if (moved) u.door = moved; else units.splice(units.indexOf(u), 1);
    }
    // A flat that a setback or an odd plot edge cut below its type's legal
    // minimum is re-declared as the largest type it does satisfy, the way an
    // architect would: a 70 m² "three-room" is a two-room. Only a flat too
    // small for any type stays flagged.
    const types = (Object.keys(MIN_DWELLING_AREA) as UnitType[]).sort((x, y) => MIN_DWELLING_AREA[y] - MIN_DWELLING_AREA[x]);
    for (const u of units) if (u.kind === "apartment") {
      u.usable = u.area * UNIT_NET;
      if (u.usable < MIN_DWELLING_AREA[u.type!]) u.type = types.find((t) => t < u.type! && MIN_DWELLING_AREA[t] <= u.usable!) ?? u.type;
      u.legalMin = MIN_DWELLING_AREA[u.type!];
      u.ok = u.usable >= u.legalMin;
    }
    // Too small for any type: a small piece joins the smallest flat it shares
    // a wall with, as one room; a large one stays terrace below. Joining a
    // 40 m² remnant to a neighbour as its "bedroom" makes a flat no one
    // would draw.
    for (const u of units.filter((x) => x.kind === "apartment" && !x.ok && x.area <= 12)) {
      const touching = units
        .filter((v) => v !== u && v.kind === "apartment" && v.ok && join(v.polygon, u.polygon).length === 1)
        .sort((x, y) => x.area - y.area)[0];
      if (!touching) continue;
      touching.polygon = join(touching.polygon, u.polygon);
      touching.area = area(touching.polygon);
      touching.usable = touching.area * UNIT_NET;
      // the piece becomes part of the room it shares the longest wall with, not a room of its own
      // (as a "bedroom" it was a room reached through the living room)
      const wallTo = (r: Room) => area(intersection(grow(u.polygon, 0.05), r.polygon));
      const host = [...(touching.rooms ?? [])].filter((r) => r.kind !== "bath").sort((a, b) => wallTo(b) - wallTo(a))[0];
      if (host && wallTo(host) > 0.02) host.polygon = join(host.polygon, u.polygon);
      else touching.rooms = [...(touching.rooms ?? []), { kind: "storage", polygon: u.polygon }];
      units.splice(units.indexOf(u), 1);
    }
    // Still too small and alone: terrace, not a dwelling.
    for (const u of units.filter((x) => x.kind === "apartment" && !x.ok)) units.splice(units.indexOf(u), 1);
    // A setback can cut away exactly the service strip. A flat with no bath is
    // not a flat, so one is carved next to the front door, from whatever room
    // is there: a square of BATH_WIDTH running from the door into the flat.
    for (const u of units) if (u.kind === "apartment" && u.door && !u.rooms?.some((r) => r.kind === "bath")) {
      const pts = u.polygon.flatMap((p) => p[0]);
      const c: Pt = [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
      const len = Math.hypot(c[0] - u.door[0], c[1] - u.door[1]) || 1;
      const g: Frame = frame(u.door, [u.door[0] + (c[1] - u.door[1]) / len, u.door[1] - (c[0] - u.door[0]) / len]);
      // in this frame t runs from the door towards the middle of the flat
      const bath = intersection(rect(g, -BATH_WIDTH / 2, BATH_WIDTH / 2, -Math.min(BATH_WIDTH, len), 0), u.polygon);
      const flip = area(bath) < 1 ? intersection(rect(g, -BATH_WIDTH / 2, BATH_WIDTH / 2, 0, Math.min(BATH_WIDTH, len)), u.polygon) : bath;
      u.rooms = [...(u.rooms ?? []).map((r) => ({ ...r, polygon: difference(r.polygon, flip) })).filter((r) => area(r.polygon) > 0.3), { kind: "bath", polygon: flip }];
    }
    for (const u of units) if (u.kind === "apartment") splitRooms(u);
    // A flat re-declared as fewer rooms, or merged with a neighbour, must not
    // keep more bedrooms than its type: the extra one opens into the living room.
    // It joins the living room where they share a wall — one open room, not
    // two rooms both called living — and is a second living room only where
    // they do not touch.
    for (const u of units) if (u.kind === "apartment" && u.rooms) {
      let beds = Number(u.type) - 1;
      const surplus: Room[] = [];
      u.rooms = u.rooms.filter((r) => (r.kind === "bedroom" && beds-- <= 0 ? (surplus.push(r), false) : true));
      for (const r of surplus) {
        const wall = (x: Room) => area(intersection(grow(r.polygon, 0.05), x.polygon)) / 0.05;
        const living = u.rooms.filter((x) => x.kind === "living").map((x) => [x, wall(x)] as const).sort((a, b) => b[1] - a[1])[0];
        const joined = living && living[1] >= 1 ? join(living[0].polygon, r.polygon) : [];
        if (joined.length === 1) living[0].polygon = joined;
        else u.rooms.push({ ...r, kind: "living" });
      }
    }
    // Daylight: the living room is wherever the light is. If the room planned
    // as living has no outside wall on this storey (a wing or a setback took
    // it), it swaps with the habitable room that has one; a flat with no
    // outside wall to any habitable room is not a dwelling.
    for (const u of units.filter((x) => x.kind === "apartment")) {
      const rooms = u.rooms ?? [];
      // an outside wall long enough to take a window (openings need 1.2 m)
      const lit = (r: Room) => facadeEdges(r.polygon, s.footprint).some(([p, q]) => Math.hypot(q[0] - p[0], q[1] - p[1]) >= 1.3);
      const living = rooms.find((r) => r.kind === "living");
      if (living && lit(living)) continue;
      const other = rooms.filter((r) => (r.kind === "bedroom" || r.kind === "kitchen" || r.kind === "living") && lit(r)).sort((a, b) => area(b.polygon) - area(a.polygon))[0];
      if (!other) { units.splice(units.indexOf(u), 1); continue; }
      if (living) living.kind = other.kind === "kitchen" ? "kitchen" : "bedroom";
      other.kind = "living";
    }
    // A flat is declared as the rooms it has. Bedrooms are never cut out of
    // the living room: a bedroom entered through the living room, carved
    // from its window wall, is the plan nobody sells. A flat with fewer
    // bedrooms than its type is re-declared a smaller type; a living room
    // left too large (with no kitchen of its own) gives the kitchen a bay
    // of its own on the facade instead — the room a living room is most
    // often joined to, and the one that belongs next to it.
    for (const u of units) if (u.kind === "apartment" && u.rooms && u.type) {
      const living = u.rooms.find((r) => r.kind === "living");
      if (living && !u.rooms.some((r) => r.kind === "kitchen") && area(living.polygon) > LIVING_MAX) {
        const cut = carveBay(living.polygon, s.footprint, u.door, KITCHEN_CUT, true);
        if (cut) { living.polygon = cut.rest; u.rooms.push({ kind: "kitchen", polygon: cut.bay }); }
      }
      const beds = u.rooms.filter((r) => r.kind === "bedroom").length;
      if (beds < Number(u.type) - 1) {
        const t = String(beds + 1) as UnitType;
        u.type = t;
        u.legalMin = MIN_DWELLING_AREA[t];
      }
    }
    // A bedroom is at least 2.7 m on its short side and no longer than twice
    // its width (the reference: 2.8–3.3 m, rarely past 1:1.8). A narrower
    // "bedroom" is a corridor with a bed in it: it joins the room beside it,
    // or is a dressing, and the flat is declared as the rooms it really has.
    for (const u of units) if (u.kind === "apartment" && u.rooms) {
      for (const r of [...u.rooms]) {
        if (r.kind !== "bedroom" || roomFits(r.polygon)) continue;
        const wallTo = (x: Room) => area(intersection(grow(r.polygon, 0.05), x.polygon));
        const host = u.rooms.filter((x) => x !== r && (x.kind === "bedroom" || x.kind === "living")).sort((a, b) => wallTo(b) - wallTo(a))[0];
        const joined = host && wallTo(host) > 0.1 ? join(host.polygon, r.polygon) : [];
        if (joined.length === 1 && roomFits(joined, host.kind === "living" ? 0 : 1)) { host.polygon = joined; u.rooms = u.rooms.filter((x) => x !== r); }
        else r.kind = "storage";
      }
      const beds = u.rooms.filter((x) => x.kind === "bedroom").length;
      if (beds < Number(u.type) - 1) { u.type = String(beds + 1) as UnitType; u.legalMin = MIN_DWELLING_AREA[u.type]; }
    }
    // A bedroom is 12–17 m² in what sells (master to about 19). One that the
    // steps above made larger — a wedge, a remnant, a clipped end flat —
    // keeps about 16 m² by its window and its back becomes its dressing.
    for (const u of units) if (u.kind === "apartment" && u.rooms) {
      for (const r of [...u.rooms]) {
        if (r.kind !== "bedroom" || area(r.polygon) <= BEDROOM_MAX) continue;
        const wall = facadeEdges(r.polygon, s.footprint).sort(([a, b], [c, d]) => Math.hypot(d[0] - c[0], d[1] - c[1]) - Math.hypot(b[0] - a[0], b[1] - a[1]))[0];
        if (!wall) continue;
        let f = frame(wall[0], wall[1]);
        const L = Math.hypot(wall[1][0] - wall[0][0], wall[1][1] - wall[0][1]);
        if (distanceTo(toWorld(f, L / 2, 0.2), r.polygon) > 0) f = { ...f, n: neg(f.n) };
        const ex = extent(f, r.polygon);
        const keepDepth = Math.max(3.0, 16 / Math.max(2.8, ex.s1 - ex.s0));
        const front = intersection(r.polygon, rect(f, ex.s0 - 1, ex.s1 + 1, -1, keepDepth));
        const back = difference(r.polygon, front);
        if (front.length !== 1 || area(back) < 1.5 || back.length !== 1) continue;
        r.polygon = front;
        u.rooms.push({ kind: "storage", polygon: back });
      }
    }
    // Two halls that share a wall are one hall: a through flat's entrance
    // strip and its bedroom-side corridor are drawn separately but walked as
    // one. Halls that only touch at a corner are two, with a door between
    // them and whatever joins them — merged, the second had no door at all.
    for (const u of units) if (u.kind === "apartment" && u.rooms) {
      const halls = u.rooms.filter((r) => r.kind === "hall");
      if (halls.length < 2) continue;
      const rest = u.rooms.filter((r) => r.kind !== "hall");
      const merged: Room[] = [];
      const wall = (a: MultiPoly, b: MultiPoly) => area(intersection(grow(a, 0.05), b)) / 0.05;
      for (const h of halls) {
        const k = merged.findIndex((m) => wall(m.polygon, h.polygon) >= 1.0 && join(m.polygon, h.polygon).length === 1);
        if (k >= 0) merged[k] = { kind: "hall", polygon: join(merged[k].polygon, h.polygon) }; else merged.push(h);
      }
      u.rooms = [...rest, ...merged];
    }
    // Rule 1 of rooms.ts: you enter into the hall. A hall the front door
    // cannot reach (it lies away from the corridor) is not an entrance hall:
    // it joins the living room, and the flat is entered into an open plan.
    for (const u of units) if (u.kind === "apartment" && u.rooms && u.door) {
      const halls = u.rooms.filter((r) => r.kind === "hall");
      if (!halls.length || halls.some((h) => distanceToOutline(u.door!, h.polygon) < 0.3)) continue;
      const living = u.rooms.find((r) => r.kind === "living");
      if (!living) continue;
      for (const h of halls) {
        const joined = join(living.polygon, h.polygon);
        if (joined.length === 1) { living.polygon = joined; u.rooms = u.rooms.filter((r) => r !== h); }
      }
    }
    // Rule 4 of rooms.ts: a hall is as big as its doors need. Over the
    // allowance, keep what joins the front door to every room that opens
    // off it and give the rest to the living room, or make it a store —
    // and keep the cut only if every room can still be entered.
    for (const u of units) if (u.kind === "apartment" && u.rooms && u.door) {
      const hall = u.rooms.find((r) => r.kind === "hall");
      if (!hall || u.rooms.filter((r) => r.kind === "hall").length > 1 || area(hall.polygon) <= hallAllowance(u.area * UNIT_NET)) continue;
      const near = (p: Pt, r: number): MultiPoly => [[[[p[0] - r, p[1] - r], [p[0] + r, p[1] - r], [p[0] + r, p[1] + r], [p[0] - r, p[1] + r]]]];
      const keep: MultiPoly[] = [intersection(hall.polygon, near(u.door, 0.9))];
      for (const r of u.rooms) {
        if (r === hall) continue;
        const wall = intersection(hall.polygon, grow(r.polygon, 0.05));
        if (area(wall) < 0.03) continue;
        keep.push(intersection(hall.polygon, near(nearestOnOutline(u.door, wall), 1.0)));
      }
      const needed = intersection(hall.polygon, hull(keep.flat(3) as Pt[]));
      const excess = difference(hall.polygon, grow(needed, 0.01));
      if (area(excess) < 1 || needed.length !== 1) continue;
      const before = u.rooms.map((r) => ({ ...r }));
      hall.polygon = needed;
      for (const piece of excess) leftover([piece], u.rooms);
      u.openings = flatOpenings(u, s.footprint);
      if (reachableRooms(u).size < u.rooms.length) u.rooms = before;
    }
    // the openings, now that every room has its final shape
    for (const u of [...units]) {
      if (u.kind === "apartment") {
        splitRooms(u);
        litLiving(u, s.footprint);
        u.openings = flatOpenings(u, s.footprint);
        // A room nobody can walk into is not a room. It joins the reachable
        // room it shares the longest wall with; touching none, it leaves the
        // flat and is terrace. What is left must still be a dwelling.
        for (let pass = 0; pass < 3; pass++) {
          const rooms = u.rooms ?? [];
          const seen = reachableRooms(u);
          const lost = rooms.filter((_, k) => !seen.has(k));
          if (!lost.length) break;
          const kept = rooms.filter((_, k) => seen.has(k));
          for (const r of lost) {
            const wall = (x: Room) => area(intersection(grow(r.polygon, 0.05), x.polygon)) / 0.05;
            // never into a bath; the merged room keeps the more important of the two names
            const pref = (k: RoomKind) => { const i = MERGE_INTO[r.kind].indexOf(k); return i < 0 ? MERGE_INTO[r.kind].length : i; };
            const host = kept.filter((x) => x.kind !== "bath").map((x) => [x, wall(x)] as const)
              .sort((a, b) => pref(a[0].kind) - pref(b[0].kind) || b[1] - a[1]).find((h) => h[1] >= 0.7);
            if (host) {
              host[0].polygon = join(host[0].polygon, r.polygon);
              if (RANK.indexOf(r.kind) < RANK.indexOf(host[0].kind)) host[0].kind = r.kind;
            }
            else {
              const rest = difference(u.polygon, grow(r.polygon, 0.01));
              u.polygon = rest.length > 1 ? [rest.reduce((best, p) => (area([p]) > area([best]) ? p : best))] : rest;
            }
          }
          u.rooms = kept.map((x) => ({ ...x, polygon: clip(x.polygon, u.polygon) })).filter((x) => area(x.polygon) > 0.3);
          splitRooms(u);
          u.area = area(u.polygon);
          u.usable = u.area * UNIT_NET;
          // a bedroom absorbed into its neighbour is a room fewer
          const beds = u.rooms.filter((x) => x.kind === "bedroom").length;
          if (beds < Number(u.type) - 1) { u.type = String(beds + 1) as UnitType; u.legalMin = MIN_DWELLING_AREA[u.type]; }
          u.ok = u.usable >= u.legalMin!;
          litLiving(u, s.footprint);
          u.openings = flatOpenings(u, s.footprint);
        }
        const rooms = u.rooms ?? [];
        if (!u.ok || !rooms.some((r) => r.kind === "living") || !rooms.some((r) => r.kind === "bath") || reachableRooms(u).size < rooms.length) units.splice(units.indexOf(u), 1);
      }
      else if (u.kind === "retail") u.openings = shopOpenings(u, s.footprint);
      else if (u.kind === "core") u.openings = entranceOpening(u, s.footprint);
    }
    return { index: s.index, units };
  });

  const flats = floors.flatMap((f) => f.units.filter((u) => u.kind === "apartment"));
  const byType = prog.mix.map((m) => {
    const of = flats.filter((u) => u.type === m.type);
    const us = of.map((u) => u.usable!);
    return {
      type: m.type,
      count: of.length,
      minUsable: us.length ? Math.min(...us) : 0,
      avgUsable: us.length ? us.reduce((x, y) => x + y, 0) / us.length : 0,
      belowMin: of.filter((u) => !u.ok).length,
    };
  });
  const sunlit = flats.filter((u) => (u.sunHours ?? 0) >= SUN_HOURS);
  return {
    floors,
    byType,
    total: flats.length,
    usable: flats.reduce((t, u) => t + u.usable!, 0),
    cores: plan.filter((c) => c.kind === "core").length,
    sunless: flats.length - sunlit.length,
    sunlitUsable: sunlit.reduce((t, u) => t + u.usable!, 0),
  };
}
